import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSEO, generateLocalBusinessSchema, generateServiceSchema } from '../../utils/seo';

export default function ListingBishopscourtPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  // SEO Configuration
  useSEO({
    title: 'Bishopscourt Estate Painters - Luxury Residential Painting | Heritage Property Specialists',
    description: 'Luxury painting services in Bishopscourt, Constantia, and Wynberg. Specialized in estate properties and heritage homes. Over 18 years serving Southern Suburbs with premium craftsmanship.',
    keywords: 'painters Bishopscourt, estate painters Bishopscourt, luxury painting, heritage painting, residential painting, Southern Suburbs painters',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateLocalBusinessSchema({
          name: 'Painters Bishopscourt',
          description: 'Luxury estate and heritage property painting',
          address: 'Bishopscourt, Cape Town',
          priceRange: '$$$',
          serviceArea: ['Bishopscourt', 'Constantia', 'Wynberg', 'Claremont', 'Newlands']
        }),
        generateServiceSchema({
          name: 'Estate & Heritage Painting',
          description: 'Luxury residential and heritage property painting',
          provider: 'Painters Bishopscourt',
          areaServed: ['Bishopscourt', 'Constantia', 'Wynberg'],
          serviceType: ['Estate Painting', 'Heritage Restoration', 'Luxury Residential']
        })
      ]
    }
  });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const listing = {
    name: "Bishopscourt Luxury Painters",
    location: "Bishopscourt, Cape Town",
    website: "https://paintersbishopscourt.co.za",
    heroImage: "https://public.readdy.ai/ai/img_res/1262c6cca73fa2e94d7a676d361c9009.jpg",
    gallery: [
      "https://readdy.ai/api/search-image?query=freshly%20painted%20luxury%20estate%20exterior%20with%20white%20walls%20elegant%20architecture%20manicured%20gardens%20in%20Bishopscourt%20professional%20finish%20with%20mountain%20views&width=800&height=600&seq=bishopscourtgallery1&orientation=landscape",
      "https://readdy.ai/api/search-image?query=interior%20room%20painting%20in%20progress%20with%20professional%20painter%20applying%20premium%20paint%20to%20walls%20using%20roller%20clean%20workspace%20luxury%20home%20setting&width=800&height=600&seq=bishopscourtgallery2&orientation=landscape",
      "https://readdy.ai/api/search-image?query=heritage%20mansion%20exterior%20painting%20project%20with%20scaffolding%20professional%20painters%20working%20on%20historic%20structure%20elegant%20architecture&width=800&height=600&seq=bishopscourtgallery3&orientation=landscape",
      "https://readdy.ai/api/search-image?query=beautifully%20painted%20luxury%20interior%20living%20room%20with%20high%20ceilings%20elegant%20color%20scheme%20designer%20furniture%20natural%20lighting%20professional%20finish&width=800&height=600&seq=bishopscourtgallery4&orientation=landscape"
    ],
    services: [
      {
        title: "Luxury Estate Painting",
        description: "Transform your Bishopscourt estate with our expert luxury painting services. We specialize in high-end properties that require meticulous attention to detail and premium finishes.",
        features: ["Interior walls & ceilings", "Exterior facades & architectural details", "Trim & woodwork refinishing", "Premium color consultation"]
      },
      {
        title: "Heritage Restoration",
        description: "Specialized restoration services for historic and heritage properties in Bishopscourt. We use traditional techniques combined with modern materials to preserve architectural integrity.",
        features: ["Historic property restoration", "Period-appropriate finishes", "Architectural detail preservation", "Heritage color matching"]
      },
      {
        title: "Interior Design",
        description: "Expert color consultation and interior design painting services to help you achieve the perfect aesthetic for your luxury home. We work closely with interior designers and architects.",
        features: ["Designer collaboration", "Custom color matching", "Feature walls & decorative finishes", "High-end material application"]
      }
    ],
    serviceAreas: ["Bishopscourt", "Fernwood", "Kirstenbosch", "Constantia", "Wynberg", "Newlands"],
    about: "Bishopscourt Luxury Painters is the premier painting company serving Bishopscourt and the surrounding exclusive estates. With over 16 years of specialized experience in luxury property painting, we have established ourselves as the most trusted painting contractor for Cape Town's most prestigious homes. Our team of master craftsmen understands the exacting standards required for high-end properties and delivers results that exceed expectations.",
    aboutExtended: [
      "We specialize in luxury estate painting, heritage restoration, and designer interior finishes, offering comprehensive solutions that cater to the unique requirements of Bishopscourt's exclusive properties. Our expertise extends across interior painting with premium finishes, exterior painting of architectural masterpieces, heritage property restoration, and specialized decorative techniques that enhance the beauty and value of your estate.",
      "What sets us apart is our unwavering commitment to excellence and our use of only the finest premium paints and materials from leading international brands. Our master painters are fully trained in luxury painting techniques, heritage restoration methods, and are fully insured, giving you complete confidence throughout your project.",
      "We understand that Bishopscourt properties represent significant investments and require the highest level of craftsmanship. That's why we take the time to understand your vision, work closely with your architects and designers, and deliver flawless results that enhance the beauty and prestige of your estate. Whether you're restoring a heritage mansion or creating a contemporary masterpiece, we deliver exceptional results that stand as a testament to quality and refinement."
    ],
    ourApproach: [
      "Comprehensive estate assessment and planning",
      "Collaboration with architects and designers",
      "Premium international paint brands",
      "Master craftsman application techniques",
      "Heritage-appropriate restoration methods",
      "White-glove service and project management"
    ],
    whyChoose: [
      "16+ years luxury property experience",
      "Heritage restoration specialists",
      "Premium materials exclusively",
      "Master craftsmen team",
      "Designer collaboration",
      "Excellence guaranteed"
    ],
    testimonials: [
      {
        name: "Elizabeth Thornton",
        rating: 5,
        text: "Bishopscourt Luxury Painters exceeded our expectations in every way. Their attention to detail and craftsmanship is exceptional. They transformed our estate beautifully while respecting its heritage character."
      },
      {
        name: "James Whitfield",
        rating: 5,
        text: "Outstanding service from start to finish. The team worked seamlessly with our interior designer and delivered flawless results. The quality of workmanship is simply superb. Highly recommended for luxury properties."
      },
      {
        name: "Catherine van der Byl",
        rating: 5,
        text: "We've used several painting contractors over the years, but Bishopscourt Luxury Painters is in a class of their own. Professional, meticulous, and the results are stunning. Worth every penny."
      }
    ]
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className={`sticky top-0 z-50 border-b border-gray-800 transition-all duration-300 ${isScrolled ? 'bg-black text-white' : 'bg-black text-white'}`}>
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="text-3xl font-bold whitespace-nowrap">
              <span className="text-white">PAINTE</span>
              <span className="text-[#e9f638]">RRR</span>
            </Link>
            <nav className="hidden md:flex items-center space-x-8">
              <Link to="/" className="hover:text-[#e9f638] transition whitespace-nowrap">Home</Link>
              <Link to="/about" className="hover:text-[#e9f638] transition whitespace-nowrap">About</Link>
              <Link to="/contact" className="bg-[#e9f638] text-black px-6 py-2 rounded-lg font-semibold hover:bg-white transition whitespace-nowrap cursor-pointer">Get a Quote</Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="relative h-96 w-full overflow-hidden">
        <img 
          src={listing.heroImage}
          alt={listing.name}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 container mx-auto px-4 pb-8">
          <h1 className="text-5xl font-bold text-white mb-2">{listing.name}</h1>
          <p className="text-xl text-gray-200 flex items-center">
            <i className="ri-map-pin-line mr-2"></i>
            {listing.location}
          </p>
        </div>
      </div>

      {/* Contact Bar */}
      <div className="bg-[#e9f638] py-4">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-center items-center gap-4">
            <a 
              href={listing.website}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-black text-white px-8 py-3 rounded-lg font-bold hover:bg-gray-800 transition whitespace-nowrap cursor-pointer"
            >
              Visit Website
            </a>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="bg-gray-50 border-b border-gray-200 sticky top-[73px] z-40">
        <div className="container mx-auto px-4">
          <div className="flex space-x-8 overflow-x-auto">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'overview' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('services')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'services' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Services
            </button>
            <button
              onClick={() => setActiveTab('gallery')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'gallery' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Gallery
            </button>
            <button
              onClick={() => setActiveTab('reviews')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'reviews' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Reviews
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {activeTab === 'overview' && (
              <div>
                <section className="mb-12">
                  <h2 className="text-3xl font-bold mb-6">About Bishopscourt Luxury Painters</h2>
                  <p className="text-gray-700 text-lg leading-relaxed mb-4">{listing.about}</p>
                  {listing.aboutExtended.map((paragraph, index) => (
                    <p key={index} className="text-gray-700 text-lg leading-relaxed mb-4">{paragraph}</p>
                  ))}
                </section>

                <section className="mb-12">
                  <h3 className="text-2xl font-bold mb-6">Our Professional Approach</h3>
                  <div className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200">
                    <ul className="space-y-3">
                      {listing.ourApproach.map((step, index) => (
                        <li key={index} className="flex items-start">
                          <i className="ri-checkbox-circle-line text-[#e9f638] text-2xl mr-3 mt-1"></i>
                          <span className="text-gray-700 text-lg">{step}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </section>

                <section className="mb-12">
                  <h3 className="text-2xl font-bold mb-6">Why Choose Bishopscourt Luxury Painters</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {listing.whyChoose.map((reason, index) => (
                      <div key={index} className="flex items-start">
                        <i className="ri-check-line text-[#e9f638] text-2xl mr-3 mt-1"></i>
                        <span className="text-gray-700 text-lg">{reason}</span>
                      </div>
                    ))}
                  </div>
                </section>

                <section>
                  <h3 className="text-2xl font-bold mb-6">Areas We Serve</h3>
                  <p className="text-gray-700 text-lg mb-4">We proudly serve the following exclusive areas in Cape Town's Southern Suburbs:</p>
                  <div className="flex flex-wrap gap-3">
                    {listing.serviceAreas.map((area, index) => (
                      <span key={index} className="bg-gray-100 text-black px-4 py-2 rounded-full font-semibold">
                        {area}
                      </span>
                    ))}
                  </div>
                </section>
              </div>
            )}

            {activeTab === 'services' && (
              <div>
                <h2 className="text-3xl font-bold mb-4">Our Professional Painting Services</h2>
                <p className="text-gray-700 text-lg mb-8">We offer a comprehensive range of luxury painting services tailored to meet the exacting standards of Bishopscourt estates.</p>
                <div className="space-y-8">
                  {listing.services.map((service, index) => (
                    <div key={index} className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200 hover:border-[#e9f638] transition">
                      <h3 className="text-2xl font-bold mb-3">{service.title}</h3>
                      <p className="text-gray-700 mb-4 text-lg">{service.description}</p>
                      <h4 className="font-semibold text-lg mb-3">What We Offer:</h4>
                      <ul className="space-y-2">
                        {service.features.map((feature, idx) => (
                          <li key={idx} className="flex items-center text-gray-700">
                            <i className="ri-arrow-right-s-line text-[#e9f638] mr-2"></i>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'gallery' && (
              <div>
                <h2 className="text-3xl font-bold mb-8">Project Gallery</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {listing.gallery.map((image, index) => (
                    <div key={index} className="h-80 w-full overflow-hidden rounded-lg shadow-lg hover:shadow-2xl transition">
                      <img 
                        src={image}
                        alt={`Project ${index + 1}`}
                        className="w-full h-full object-cover object-top hover:scale-110 transition-transform duration-500 cursor-pointer"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'reviews' && (
              <div>
                <h2 className="text-3xl font-bold mb-8">Customer Reviews</h2>
                <div className="space-y-6">
                  {listing.testimonials.map((testimonial, index) => (
                    <div key={index} className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200">
                      <div className="flex items-center mb-4">
                        <div className="flex mr-4">
                          {[...Array(testimonial.rating)].map((_, i) => (
                            <i key={i} className="ri-star-fill text-[#e9f638] text-xl"></i>
                          ))}
                        </div>
                        <span className="font-bold text-lg">{testimonial.name}</span>
                      </div>
                      <p className="text-gray-700 text-lg leading-relaxed">{testimonial.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-lg p-6 sticky top-24">
              <h3 className="text-2xl font-bold mb-6">Contact Information</h3>
              
              <div className="space-y-4 mb-6">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center">
                    <i className="ri-map-pin-line text-2xl text-[#e9f638]"></i>
                  </div>
                  <div>
                    <p className="font-semibold">Service Areas</p>
                    <p className="text-gray-600">Bishopscourt, Fernwood, Kirstenbosch</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center">
                    <i className="ri-time-line text-2xl text-[#e9f638]"></i>
                  </div>
                  <div>
                    <p className="font-semibold">Business Hours</p>
                    <p className="text-gray-600">Mon-Fri: 8AM-5PM</p>
                    <p className="text-gray-600">Sat: By Appointment</p>
                  </div>
                </div>
              </div>
              
              <a 
                href={listing.website}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full bg-[#e9f638] text-black text-center py-3 rounded-lg font-bold hover:bg-black hover:text-[#e9f638] transition-colors duration-300 whitespace-nowrap cursor-pointer"
              >
                Visit Website
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-black text-white border-t border-gray-800">
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-2xl font-bold mb-4">
                <span className="text-white">PAINTE</span>
                <span className="text-[#e9f638]">RRR</span>
              </h3>
              <p className="text-gray-400">South Africa's premier directory for professional house painters.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Quick Links</h4>
              <ul className="space-y-2">
                <li><Link to="/" className="text-gray-400 hover:text-[#e9f638] cursor-pointer">Home</Link></li>
                <li><Link to="/about" className="text-gray-400 hover:text-[#e9f638] cursor-pointer">About</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-[#e9f638] cursor-pointer">Contact</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Service Areas</h4>
              <ul className="space-y-2 text-gray-400">
                <li>Bishopscourt</li>
                <li>Fernwood</li>
                <li>Kirstenbosch</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Connect</h4>
              <p className="text-gray-400">Find the perfect painter for your project today.</p>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center text-sm text-gray-400">
            <p>© 2025 PAINTERRR. All rights reserved.</p>
            <div className="flex space-x-4 mt-4 md:mt-0">
              <a href="https://leaderr.co/" target="_blank" rel="noopener noreferrer" className="hover:text-[#e9f638] cursor-pointer">
                Powered by leaderr
              </a>
              <span>|</span>
              <a href="https://seostudio.co.za/" target="_blank" rel="noopener noreferrer" className="hover:text-[#e9f638] cursor-pointer">
                SEO by SEO Studio
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
